import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { LogOut, ShieldCheck } from "lucide-react";

// Partner view (2026-10, partner-newflex-view). Read-only: nothing on these
// pages changes anything. The marker below lets the after-deploy check find
// the partner pages in the built front end.
export const PARTNER_VIEW_MARKER = "partner-newflex-view-v1";

// Dates arrive as "YYYY-MM-DD" and visit times as the clinic's clock face
// ("HH:MM"). Format them as text, never through Date(), so no time zone
// conversion can shift a day or an hour.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const formatDay = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
};
export const formatClock = (hhmm) => {
  if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return "";
  let [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ampm}`;
};
export const formatVisit = (v) => (v ? `${formatDay(v.date)}, ${formatClock(v.time)}` : "None booked");

export function PartnerNotice({ status }) {
  const text = status === 404
    ? "This page isn't available for your account yet. Please contact WestStar."
    : "We couldn't load this page just now. Please try again in a few minutes.";
  return (
    <div data-testid="partner-notice" className="rounded-lg border border-slate-200 bg-white p-6 text-slate-600">
      {text}
    </div>
  );
}

export default function PartnerShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50" data-testid="partner-shell" data-marker={PARTNER_VIEW_MARKER}>
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div>
            <div className="text-lg font-semibold text-slate-900">WestStar Physical Therapy</div>
            <div className="text-xs text-slate-500">Referral partner view · read only</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-500 sm:inline">{user?.email || user?.username}</span>
            <Link
              to="/partner/2-step"
              data-testid="partner-2step-link"
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
            >
              <ShieldCheck className="h-4 w-4" /> 2-step sign-in
            </Link>
            <button
              type="button"
              data-testid="partner-logout"
              onClick={() => { logout(); navigate("/login"); }}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6">{children}</main>
    </div>
  );
}
